<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isAConversationRoute,
  isAInboxViewRoute,
} from 'dashboard/helper/routeHelpers';
import { useEventListener } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();

const RECONNECTED_BANNER_TIMEOUT = 2000;

const notificationStatus = ref(navigator.onLine ? null : 'internetOffline');
const isDisconnected = ref(false);
let reconnectTimeout = null;

const bannerText = computed(() => {
  switch (notificationStatus.value) {
    case 'internetOffline':
      return t('NETWORK.NOTIFICATION.INTERNET_OFFLINE');
    case 'internetRestored':
      return t('NETWORK.NOTIFICATION.INTERNET_RESTORED');
    case 'realtimeOffline':
      return t('NETWORK.NOTIFICATION.REALTIME_OFFLINE');
    case 'realtimeReconnecting':
      return t('NETWORK.NOTIFICATION.REALTIME_RECONNECTING');
    case 'realtimeRestored':
      return t('NETWORK.NOTIFICATION.REALTIME_RESTORED');
    default:
      return '';
  }
});

const isRestored = computed(() =>
  ['internetRestored', 'realtimeRestored'].includes(notificationStatus.value)
);
const iconName = computed(() => (isRestored.value ? 'wifi' : 'wifi-off'));
const canRefresh = computed(
  () =>
    notificationStatus.value === 'internetOffline' ||
    notificationStatus.value === 'realtimeOffline'
);

const refreshPage = () => {
  window.location.reload();
};

const clearReconnectTimeout = () => {
  clearTimeout(reconnectTimeout);
  reconnectTimeout = null;
};

const closeNotification = () => {
  notificationStatus.value = null;
  clearReconnectTimeout();
};

const isInAnyOfTheRoutes = routeName => {
  return (
    isAConversationRoute(routeName, true) || isAInboxViewRoute(routeName, true)
  );
};

const updateWebsocketStatus = () => {
  clearReconnectTimeout();
  isDisconnected.value = true;
  notificationStatus.value = navigator.onLine
    ? 'realtimeOffline'
    : 'internetOffline';
};

const handleReconnectionCompleted = () => {
  clearReconnectTimeout();
  isDisconnected.value = false;
  notificationStatus.value = 'realtimeRestored';
  reconnectTimeout = setTimeout(closeNotification, RECONNECTED_BANNER_TIMEOUT);
};

const handleReconnecting = () => {
  clearReconnectTimeout();
  if (isInAnyOfTheRoutes(route.name)) {
    notificationStatus.value = 'realtimeReconnecting';
  } else {
    handleReconnectionCompleted();
  }
};

const updateOnlineStatus = event => {
  clearReconnectTimeout();

  // Case: Websocket is not disconnected
  // If the app goes offline, show the notification
  // If the app goes online, close the notification

  // Case: Websocket is disconnected
  // If the app goes offline, show the notification
  // If the app goes online but the websocket is disconnected, don't close the notification
  // If the app goes online and the websocket is not disconnected, close the notification

  if (event.type === 'offline') {
    notificationStatus.value = 'internetOffline';
  } else if (isDisconnected.value) {
    notificationStatus.value = 'realtimeOffline';
  } else {
    notificationStatus.value = 'internetRestored';
    reconnectTimeout = setTimeout(
      closeNotification,
      RECONNECTED_BANNER_TIMEOUT
    );
  }
};

useEventListener('online', updateOnlineStatus);
useEventListener('offline', updateOnlineStatus);
useEmitter(BUS_EVENTS.WEBSOCKET_DISCONNECT, updateWebsocketStatus);
useEmitter(
  BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
  handleReconnectionCompleted
);
useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, handleReconnecting);

onBeforeUnmount(() => {
  clearReconnectTimeout();
});
</script>

<template>
  <transition name="network-notification-fade" tag="div">
    <div v-show="notificationStatus" class="fixed z-50 top-2 left-2 group">
      <div
        class="relative flex items-center justify-between w-full px-2 py-1 bg-n-amber-4 dark:bg-n-amber-8 rounded-lg shadow-lg"
      >
        <fluent-icon :icon="iconName" class="text-n-amber-12" size="18" />
        <span class="px-2 text-xs font-medium tracking-wide text-n-amber-12">
          {{ bannerText }}
        </span>
        <Button
          v-if="canRefresh"
          ghost
          sm
          amber
          icon="i-lucide-refresh-ccw"
          :title="$t('NETWORK.BUTTON.REFRESH_PAGE')"
          :aria-label="$t('NETWORK.BUTTON.REFRESH_PAGE')"
          class="!text-n-amber-12 dark:!text-n-amber-9"
          @click="refreshPage"
        />

        <Button
          ghost
          sm
          amber
          icon="i-lucide-x"
          :title="$t('NETWORK.BUTTON.CLOSE')"
          :aria-label="$t('NETWORK.BUTTON.CLOSE')"
          class="!text-n-amber-12 dark:!text-n-amber-9"
          @click="closeNotification"
        />
      </div>
    </div>
  </transition>
</template>
